import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../core/models/base-state/base-state';
import { setPatientIdSuccess } from '../../../user/store/actions/user.actions';
import { appointmentSort } from '../../data/appointment-sort';
import { Appointment } from '../../models/appointment';
import {
  getNextPatientAppointment,
  getNextPatientAppointmentFailure,
  getNextPatientAppointmentSuccess,
  getPastPatientAppointmentsFailure,
  getPastPatientAppointmentsSuccess,
  getPatientAppointments,
  getUpcomingPatientAppointmentsFailure,
  getUpcomingPatientAppointmentsSuccess,
  loadMorePastAppointments,
} from '../actions/appointments.actions';

export const appointmentFeatureKey = 'appointment';

export interface AppointmentState extends EntityState<Appointment> {
  baseState: baseState.BaseState;
  isLoadingUpcomingAppointments: boolean;
  isLoadingPastAppointments: boolean;
  isLoadingMorePastAppointments: boolean;
}

export const appointmentAdapter = createEntityAdapter<Appointment>({
  selectId: appointment => appointment.appointmentId,
});

export const initialState: AppointmentState = appointmentAdapter.getInitialState({
  baseState: baseState.createBaseState(appointmentSort),
  isLoadingUpcomingAppointments: false,
  isLoadingPastAppointments: false,
  isLoadingMorePastAppointments: false,
});

export const appointmentReducer = createReducer<AppointmentState>(
  initialState,

  on(getPatientAppointments, (state): AppointmentState => {
    return {
      ...appointmentAdapter.removeAll(state),
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
        pageNumber: 1,
      },
      isLoadingUpcomingAppointments: true,
      isLoadingPastAppointments: true,
      isLoadingMorePastAppointments: false,
    };
  }),

  on(getNextPatientAppointment, setPatientIdSuccess, (state): AppointmentState => {
    return {
      ...appointmentAdapter.removeAll(state),
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: true,
      },
      isLoadingUpcomingAppointments: false,
      isLoadingPastAppointments: false,
      isLoadingMorePastAppointments: false,
    };
  }),

  on(getNextPatientAppointmentSuccess, (state, { patientAppointment }): AppointmentState => {
    if (patientAppointment !== null) return appointmentAdapter.addOne(patientAppointment, state);
    return state;
  }),

  on(getNextPatientAppointmentSuccess, getUpcomingPatientAppointmentsSuccess, (state): AppointmentState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
      isLoadingUpcomingAppointments: false,
    };
  }),

  on(getNextPatientAppointmentSuccess, getUpcomingPatientAppointmentsSuccess, (state): AppointmentState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        isLoading: false,
      },
    };
  }),

  on(getPastPatientAppointmentsSuccess, (state, { pageNumber, isAtEndOfData, totalRecordCount }): AppointmentState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: false,
        pageNumber,
        isAtEndOfData,
        totalRecordCount,
        pageSize: baseState.DEFAULT_PAGE_SIZE,
      },
      isLoadingPastAppointments: false,
      isLoadingMorePastAppointments: false,
    };
  }),

  on(getNextPatientAppointmentFailure, (state, { error }): AppointmentState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
      isLoadingUpcomingAppointments: false,
      isLoadingPastAppointments: false,
      isLoadingMorePastAppointments: false,
    };
  }),

  on(getUpcomingPatientAppointmentsFailure, (state, { error }): AppointmentState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
      isLoadingUpcomingAppointments: false,
    };
  }),

  on(getPastPatientAppointmentsFailure, (state, { error }): AppointmentState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error,
        isLoading: false,
      },
      isLoadingPastAppointments: false,
      isLoadingMorePastAppointments: false,
    };
  }),

  on(
    getUpcomingPatientAppointmentsSuccess,
    getPastPatientAppointmentsSuccess,
    (state, { patientAppointments }): AppointmentState => {
      return appointmentAdapter.addMany(patientAppointments, state);
    }
  ),

  on(loadMorePastAppointments, (state): AppointmentState => {
    return {
      ...state,
      baseState: {
        ...state.baseState,
        error: null,
        pageNumber: (state.baseState.pageNumber ?? 1) + 1,
      },
      isLoadingPastAppointments: false,
      isLoadingMorePastAppointments: true,
    };
  })
);
